import { useState } from "react";
import Modal from "../../shared/components/Modal";
import { toast } from "react-toastify";
import appApi from "../../api";
import Select from "shared/components/Select";
import FileUpload from "shared/components/FileUpload";
import { UploadFileOutlined } from "@mui/icons-material";
import InfoIconWithTooltip from "shared/components/InfoIconWithTooltip";

/**
 * @typedef {{
 *  name: string;
 *  collector_key: string;
 *  id_col: string;
 *  csv: File;
 * }} UploadFormData
 */

/** @type {UploadFormData} */
const DEFAULT_UPLOAD_FORM = {
    name: "",
    collector_key: "",
    id_col: "",
    csv: undefined,
};

/**
 *
 * @param {object} props
 * @param {string} props.title
 * @param {(upload: object) => void} props.onUploadCreated Called when a bulk upload is created.
 * @param {() => void} props.onClose
 * @param {boolean} props.isOpen Whether the dialog should be open or not.
 */
function BulkGeneratorDialog({ title, onUploadCreated, onClose, isOpen, collectors }) {
    /** @type {[UploadFormData, React.Dispatch<React.SetStateAction<UploadFormData>>]} */
    const [uploadForm, setUploadForm] = useState({ ...DEFAULT_UPLOAD_FORM });
    const [generating, setGenerating] = useState(false);

    function validateForm() {
        const errors = [];

        if (!uploadForm.name) {
            errors.push("Upload name is required");
        }
        if (!uploadForm.collector_key) {
            errors.push("A collector is required");
        }
        if (!uploadForm.id_col) {
            errors.push("An ID column name is required");
        }

        return errors;
    }

    async function onSaveUpload() {
        let closeModal = true;

        const errors = validateForm();
        if (errors.length > 0) {
            toast.error(
                <>
                    {errors.length === 1 ? (
                        <>{errors[0]}</>
                    ) : (
                        <ul style={{ marginLeft: "24px" }}>
                            {errors.map((error, index) => (
                                <li key={`generate-collector-error-${index}`} style={{ listStyleType: "disc" }}>
                                    {error}
                                </li>
                            ))}
                        </ul>
                    )}
                </>,
            );
            return;
        }

        try {
            setGenerating(true);
            const createdUpload = await createUpload();
            onUploadCreated?.(createdUpload);
        } catch (error) {
            console.error(error);

            closeModal = false; // Keep the modal open for better UX

            if (error.response?.status === 409) {
                toast.error("A bulk upload with this name already exists");
            } else {
                toast.error(error.response?.data?.message ?? "Failed to generate bulk upload");
            }
        } finally {
            setGenerating(false);
            if (closeModal) {
                close();
            }
        }
    }

    async function createUpload() {
        const formData = new FormData();
        formData.append("name", uploadForm.name);
        formData.append("link_key", uploadForm.collector_key);
        formData.append("input_csv", uploadForm.csv);
        formData.append("id_col", uploadForm.id_col);
        const response = await appApi.post("/company/generate/bulk", formData, {
            headers: { "content-type": "multipart/form-data" },
        });
        return response.data;
    }

    function close() {
        onClose?.();
        resetForm();
    }

    function resetForm() {
        setUploadForm({ ...DEFAULT_UPLOAD_FORM });
    }

    return (
        <Modal
            title={title}
            isOpen={isOpen}
            cancelButtonProps={{ disabled: generating, onClick: close }}
            onClickOutside={(_) => {}}
            customButtonProps={[
                {
                    children: "Create",
                    loading: generating,
                    onClick: onSaveUpload,
                    disabled: !uploadForm.name || !uploadForm.collector_key || !uploadForm.csv || !uploadForm.id_col,
                },
            ]}
            style={{
                width: "calc(15% + 200px)",
                minWidth: "fit-content",
                maxWidth: "100%",
            }}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div>
                    <label
                        htmlFor="collector-name"
                        className="paragraph-5-semibold"
                        style={{ color: "var(--colors-primary-slate-700)" }}
                    >
                        Name
                    </label>
                    <input
                        id="upload-name"
                        type="text"
                        className="text-field"
                        value={uploadForm.name}
                        onChange={(e) => {
                            setUploadForm({ ...uploadForm, name: e.target.value });
                        }}
                        style={{ width: "100%", color: "var(--colors-primary-slate-900)" }}
                    />
                </div>
                <div>
                    <div className="flex flex-row items-center">
                        <label
                            htmlFor="id-col"
                            className="paragraph-5-semibold"
                            style={{ color: "var(--colors-primary-slate-700)" }}
                        >
                            ID Column
                        </label>
                        <InfoIconWithTooltip content="CSV column to use as unique ID (user ID, email, etc.)." />
                    </div>
                    <input
                        id="id-col"
                        type="text"
                        className="text-field"
                        value={uploadForm.id_col}
                        onChange={(e) => {
                            setUploadForm({ ...uploadForm, id_col: e.target.value });
                        }}
                        style={{ width: "100%", color: "var(--colors-primary-slate-900)" }}
                    />
                </div>
                <div>
                    <label className="paragraph-5-semibold" style={{ color: "var(--colors-primary-slate-700)" }}>
                        Collector
                    </label>
                    <Select
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
                        menuPortalTarget={document.body}
                        options={collectors.map((collector) => ({
                            label: collector.link_name,
                            value: collector.link_key,
                        }))}
                        onChange={(value) => {
                            setUploadForm({ ...uploadForm, collector_key: value });
                        }}
                        noOptionsMessage={() => "No collectors found. Please create one before using this feature."}
                    />
                </div>
                <div>
                    <label className="paragraph-5-semibold" style={{ color: "var(--colors-primary-slate-700)" }}>
                        Input file
                    </label>
                    {uploadForm.csv && (
                        <div className="m-2">
                            <p className="paragraph-5">
                                <UploadFileOutlined />
                                <span>{uploadForm.csv.name}</span>
                            </p>
                        </div>
                    )}
                    <FileUpload
                        onUpload={(file) => setUploadForm({ ...uploadForm, csv: file })}
                        allowedFileTypes={{ ["CSV"]: "text/csv" }}
                        maxFileSizeMb={50}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default BulkGeneratorDialog;
